import { Show, createEffect, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { Button } from '../../../../ui-components/button/button';
import { Modal } from '../../../../ui-components/modal/modal';
import { Heading } from '../../../../ui-components/heading/heading';
import { pdfStore, setPdfStore } from '../../pdf-store';
import { fieldShellClasses } from '../../../forms-tw/forms-fields/primitives';

const INPUT_ID = 'pdf-builder-title-input';
const ERROR_ID = 'pdf-builder-title-error';
const WARNING_ID = 'pdf-builder-title-warning';

export const TitleModal = (props: {
    createNewCollection: () => void;
    saveExistingCollection: () => void;
}) => {

    const { localize } = useContext(AppContext);
    let input: HTMLInputElement | undefined;

    const [ filenameError, setFilenameError ] = createSignal('');
    const [ filenameWarning, setFilenameWarning ] = createSignal('');

    createEffect(() => {
        if (pdfStore.status === 'naming') {
            input?.focus();
        }
    });

    const updateTitle = (e: Event) => {
        if (filenameError()) {
            setFilenameError('');
        }

        const target = e.target as HTMLInputElement;
        if (!target.value) {
            return;
        }

        const ok = validateTitle(target.value);
        if (!ok) {
            return;
        }

        setPdfStore('title',target.value);
    };

    const validateTitle = (title: string) => {
        const regex = /^[-a-zA-Z0-9_ ]*$/;
        const ok = regex.test(title);

        if (!ok) {
            setFilenameError(localize('pdf-title-allowed-characters', 'Please only use alphanumeric characters, dashes, underscores and spaces'));
            return false;
        }

        return true;
    };

    const saveBuild = async () => {
        if (duplicateTitle()) {
            props.saveExistingCollection();
            return;
        };

        setPdfStore('status', 'creating');
        props.createNewCollection();
    };

    const duplicateTitle = () => {
        setFilenameWarning('');
        setFilenameError('');

        const existingTitles = pdfStore.collections.map((collection) => collection.title);
        const isDuplicate = existingTitles.includes(pdfStore.title);

        if (isDuplicate) {
            setFilenameWarning(localize('pdf-same-name-warning', 'A collection with this name already exists. Do you want to overwrite it?'));
            return true;
        }

        return isDuplicate;
    };

    const closeModal = () => {
        setPdfStore('status', '');
    };

    const describedBy = () => [
        filenameError() ? ERROR_ID : '',
        filenameWarning() ? WARNING_ID : '',
    ].filter(Boolean).join(' ') || undefined;

    const title = () => localize('pdf-name-of-file', 'Name your file');

    /* DS Modal, small (Storybook `components-modal--size-small`) wrapping a DS FormKit
       text input (`components-formkit-text-input`). */
    return (
        <Modal
            visible={ pdfStore.status === 'naming' }
            onClose={ closeModal }
            size='sm'
            align='start'
            darkenBackground={ true }
            ariaLabel={ title() }
            header={
                <Heading tag="h2" variant="heading-lg" noBlockSpacing>
                    { title() }
                </Heading>
            }
        >
            <div class="flex flex-col gap-cvi-6 pt-cvi-6">
                <div class="flex flex-col gap-cvi-3">
                    <label for={INPUT_ID} class="label-md font-cvi-medium text-cvi-fg-default">
                        { localize('name', 'Name') }
                    </label>
                    <input
                        id={INPUT_ID}
                        ref={input}
                        type="text"
                        class={fieldShellClasses(!!filenameError())}
                        value={ pdfStore.title }
                        onInput={ ((e) => updateTitle(e)) }
                        placeholder={ localize('name', 'Name') }
                        aria-invalid={ !!filenameError() }
                        aria-describedby={ describedBy() }
                        autocomplete="off"
                    />
                    <Show when={filenameError()}>
                        <p id={ERROR_ID} role="alert" class="m-0 body-sm font-cvi-regular text-cvi-fg-danger">{filenameError()}</p>
                    </Show>
                    <Show when={filenameWarning()}>
                        <p id={WARNING_ID} role="status" class="m-0 body-sm font-cvi-regular text-cvi-fg-muted">{filenameWarning()}</p>
                    </Show>
                </div>

                <div class="flex flex-wrap justify-end gap-cvi-4">
                    <Button
                        label={ localize('cancel', 'Cancel') }
                        variant='secondary-neutral'
                        onClick={ closeModal }
                        noBlockSpacing
                    />
                    <Button
                        disabled={ !pdfStore.title }
                        label={ duplicateTitle()
                            ? localize('pdf-overwrite-existing', 'Overwrite')
                            : localize('pdf-builder-create', 'Create')
                        }
                        variant='primary'
                        onClick={ saveBuild }
                        noBlockSpacing
                    />
                </div>
            </div>
        </Modal>
    );
};
